import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { VaultApiService } from '@app/core/services/api/vault-api.service';
import {
  StandardAccountDictionaryProposal,
  StandardAccountDictionaryProposalRevertResult,
  StandardAccountDictionaryProposalStatus,
  StandardAccountDictionaryProposalSummary,
  StandardAccountDictionaryVersion,
  StandardAccountDictionaryVersionDetail,
} from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import {
  ActivateStandardAccountDictionaryVersionRequest,
  ApplyStandardAccountDictionaryProposalRequest,
  RejectStandardAccountDictionaryProposalRequest,
  RevertStandardAccountDictionaryProposalRequest,
  UpdateStandardAccountDictionaryEntryRequest,
} from '@interfaces/accounting-data-standardization/requests/standard-account-dictionary.request';

/**
 * Gestão do dicionário do plano padrão no banco (PBI 3423, DPA-DIC-DB): versões, edição manual de verbete, volta para
 * versão anterior e propostas do revisor do De/Para. Irmão do `AccountingDeParaGovernanceService` (mesmo controller no
 * backend); LIST para ler, MANAGE para mudar.
 */
@Injectable({ providedIn: 'root' })
export class StandardAccountDictionaryGovernanceService {
  private readonly api = inject(VaultApiService);
  private readonly BASE = 'accounting-document-management/standard-accounts/dictionary';

  /** Versões do dicionário, sem os verbetes (a ativa tem `status: 'Active'`). */
  getVersions(): Observable<StandardAccountDictionaryVersion[] | null> {
    return this.api.get<StandardAccountDictionaryVersion[]>(`${this.BASE}/versions`);
  }

  /** Uma versão com todos os verbetes (DRE e Balanço). */
  getVersion(number: number): Observable<StandardAccountDictionaryVersionDetail | null> {
    return this.api.get<StandardAccountDictionaryVersionDetail>(`${this.BASE}/versions/${number}`);
  }

  /** Edita um verbete: cria versão nova ativa (origem `Manual`). */
  updateEntry(
    body: UpdateStandardAccountDictionaryEntryRequest
  ): Observable<StandardAccountDictionaryVersion | null> {
    return this.api.put<StandardAccountDictionaryVersion>(`${this.BASE}/entries`, body);
  }

  /** Volta para a versão `number`: copia o snapshot dela como versão nova (origem `Rollback`). */
  activateVersion(
    number: number,
    body: ActivateStandardAccountDictionaryVersionRequest
  ): Observable<StandardAccountDictionaryVersion | null> {
    return this.api.post<StandardAccountDictionaryVersion>(
      `${this.BASE}/versions/${number}/activate`,
      body
    );
  }

  /**
   * Propostas do revisor; `status` ausente traz todas. `toReview` (task 3452) com `status: 'Applied'` traz só as
   * aplicadas sozinhas que a revisão ainda não manteve nem desfez.
   */
  getProposals(
    status?: StandardAccountDictionaryProposalStatus | null,
    toReview = false
  ): Observable<StandardAccountDictionaryProposal[] | null> {
    const params: Record<string, unknown> = {};
    if (status) params['status'] = status;
    if (toReview) params['toReview'] = true;
    return this.api.get<StandardAccountDictionaryProposal[]>(`${this.BASE}/proposals`, {}, params);
  }

  /** Quantas propostas esperam uma pessoa (`pending`) e quantas entraram sozinhas sem revisão (`toReview`). */
  getProposalSummary(): Observable<StandardAccountDictionaryProposalSummary | null> {
    return this.api.get<StandardAccountDictionaryProposalSummary>(`${this.BASE}/proposals/summary`);
  }

  /** Mantém a proposta aplicada sozinha: sai da fila "A revisar", o dicionário não muda. */
  keepProposal(id: string): Observable<StandardAccountDictionaryProposal | null> {
    return this.api.post<StandardAccountDictionaryProposal>(
      `${this.BASE}/proposals/${id}/keep`,
      {}
    );
  }

  /** Desfaz a proposta aplicada: tira o texto do verbete numa versão nova (nota obrigatória). */
  revertProposal(
    id: string,
    body: RevertStandardAccountDictionaryProposalRequest
  ): Observable<StandardAccountDictionaryProposalRevertResult | null> {
    return this.api.post<StandardAccountDictionaryProposalRevertResult>(
      `${this.BASE}/proposals/${id}/revert`,
      body
    );
  }

  /** Aplica a proposta (com o texto do revisor ou o editado): cria versão nova (origem `ReviewerProposal`). */
  applyProposal(
    id: string,
    body: ApplyStandardAccountDictionaryProposalRequest
  ): Observable<StandardAccountDictionaryVersion | null> {
    return this.api.post<StandardAccountDictionaryVersion>(
      `${this.BASE}/proposals/${id}/apply`,
      body
    );
  }

  /** Recusa a proposta com nota (obrigatória). */
  rejectProposal(
    id: string,
    body: RejectStandardAccountDictionaryProposalRequest
  ): Observable<StandardAccountDictionaryProposal | null> {
    return this.api.post<StandardAccountDictionaryProposal>(
      `${this.BASE}/proposals/${id}/reject`,
      body
    );
  }
}
